// This File contains all the functions to fetch data for filters
// It has code for fetching pick list items
// and relative filter items (from date and to date)

import { useDispatch } from 'react-redux';
import { updateAxesQueryParam } from '../../redux/ChartPoperties/ChartPropertiesActions';
import FetchData from '../ServerCall/FetchData';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import {
  setLastModifiedFilter,
  updateChartFilterRightGroupsFilters,
} from '../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import { fieldProps, IDsInfo } from '../../redux/ChartFilterGroup/ChartFilterGroupInterface';
import { IChartFIlter } from '../ChartFilterGroup/interfaces';
import { callQueryAPI } from '../../redux/ChartPoperties/ChartControlsActions';

interface useGetActionsProps {
  sortOptions: string[];
  filterFieldData: any;
  field: fieldProps & { SavedCalculationUUID?: string };
  propKey: string;
  token: string;
  uID?: string;
  itemIndex: number;
  name?: string;
  triggerSyncFilterDataFetch?: (
    calledByFilter: IChartFIlter,
    isLastModified?: boolean,
  ) => Promise<void>;
  from?: string;
  totalFiltersInGroup?: number;
  syncFilterEnabled?: boolean;
  dsInfo?: IDsInfo;
}

export const useGetActions = ({
  sortOptions,
  filterFieldData,
  field,
  propKey,
  token,
  uID,
  itemIndex,
  name = '',
  triggerSyncFilterDataFetch = () => {
    return Promise.resolve();
  },
  from = 'chartFilter',
  totalFiltersInGroup,
  syncFilterEnabled,
  dsInfo = {
    connectionId: '',
    id: '',
    workSpaceId: '',
    isFlatFileData: false,
  },
}: useGetActionsProps) => {
  const dispatch = useDispatch();

  const { calculations, chartProp, chartGroup } = useSelector((state: RootState) => ({
    calculations: state.calculations,
    chartProp: state.chartProperties,
    chartGroup: state.chartFilterGroup,
  }));

  let currentChartAxesName = uID ? 'chartAxes_' + uID : 'chartAxes';
  let link: string = '';

  if (window.location.href.includes('dashboard')) {
    let url = window.location.href.split('/').slice(-1);
    link = url.toString();
  }

  ///Construct Chart property object
  const constructChartAxesFieldObject = () => {
    return filterFieldData;
  };

  ///Fech Field data for Pick List
  const fetchFieldData = () => {
    let bodyData: any;
    const { fieldname, dataType, tableId } = field;

    if (field.SavedCalculationUUID) {
      const toFilterCalculation = calculations.savedCalculations.find(
        (calculation) => calculation.uuid === field.SavedCalculationUUID,
      )?.calculationInfo;
      bodyData = {
        tableId: tableId,
        fieldName: fieldname,
        dataType: dataType,
        filterOption: 'allValues',
        isCalculatedField: true,
        calculatedField: [
          {
            ...toFilterCalculation,
            calculatedFieldId: field.SavedCalculationUUID,
          },
        ],
      };
    } else {
      bodyData = {
        tableId: tableId,
        fieldName: fieldname,
        dataType: dataType,
        filterOption: 'allValues',
      };
    }
    if (dataType === 'timestamp' || dataType === 'date') {
      bodyData['timeGrain'] = filterFieldData.prefix || 'year';
    }

    let _url = '';

    if (from === 'chartFilter') {
      _url = `filter-options?dbconnectionid=${chartProp.properties[propKey].selectedDs.connectionId}&datasetid=${chartProp.properties[propKey].selectedDs.id}`;
    } else if (from === 'reportFilter') {
      if (link && link !== '') {
        _url = `open-link/filter-options?datasetid=${chartGroup.groups[name].dataSetId}&workspaceId=${
          dsInfo.workSpaceId
        }&${dsInfo.isFlatFileData ? '' : `dbconnectionid=${dsInfo.connectionId}`}&linkId=${link}`;
      } else {
        _url = `filter-options?datasetid=${chartGroup.groups[name].dataSetId}&workspaceId=${
          dsInfo.workSpaceId
        }&${dsInfo.isFlatFileData ? '' : `dbconnectionid=${dsInfo.connectionId}`}`;
      }
    }

    return FetchData({
      checkToken: link === '',
      requestType: 'withData',
      method: 'POST',
      url: _url,
      headers:
        link && link !== '' && from === 'reportFilter'
          ? { 'Content-Type': 'application/json', 'X-TENANT-ID': 'community' }
          : {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
      data: bodyData,
    });
  };

  ///To fetch Pick list items
  const GetPickListItems = async () => {
    if (totalFiltersInGroup && totalFiltersInGroup > 1 && syncFilterEnabled) {
      await triggerSyncFilterDataFetch(filterFieldData, true);
      return;
    }
    const { dataType } = field;
    // Avoid fetching data again if the sort options have not changed
    // if (
    //   filterFieldData['previousSortOptions'] === JSON.stringify(sortOptions) &&
    //   dataType !== 'date' &&
    //   dataType !== 'timestamp'
    // ) {
    //   return; // Return early if no changes in sort options
    // }

    let result: any = await fetchFieldData();

    if (result && result.status && result.data) {
      const key = Object.keys(result.data)[0];
      const options = result.data[key];
      result = result = options.map((value: any) => {
        return value !== null
          ? typeof value === 'boolean'
            ? value
              ? 'True'
              : 'False' // Convert boolean to string
            : value
          : '(Blank)';
      });

      // Maintain an original unfiltered copy
      filterFieldData.rawselectmembersOriginal = ['(All)', ...result];

      // Start with unfiltered data
      let tempResult = [...filterFieldData.rawselectmembersOriginal];

      // Apply multiple sort options
      if (sortOptions.includes('Sort Desc')) {
        if (typeof result[0] === 'number') {
          tempResult = ['(All)', ...[...result].sort((a, b) => b - a)];
        } else {
          tempResult = ['(All)', ...[...result].sort().reverse()];
        }
      }

      if (sortOptions.includes('Remove Blank')) {
        tempResult = tempResult.filter((val: string) => val !== '(Blank)');
      }

      if (sortOptions.includes('Blank at Bottom')) {
        const blanks = tempResult.filter((val: string) => val === '(Blank)');
        tempResult = tempResult.filter((val: string) => val !== '(Blank)').concat(blanks);
      }
      // Store sorted data in filterFieldData
      filterFieldData.rawselectmembers = [...tempResult];
      if (filterFieldData.userSelection) {
        filterFieldData.userSelection = filterFieldData.userSelection.filter((selection: any) =>
          result.includes(selection),
        );
        const enableAll = filterFieldData.userSelection.length === result.length;
        if (enableAll) filterFieldData.userSelection.unshift('(All)');
      } else filterFieldData.userSelection = [];

      // Save the sort options for comparison in future updates
      filterFieldData.previousSortOptions = JSON.stringify(sortOptions);

      if (from === 'chartFilter') {
        dispatch(
          updateAxesQueryParam(
            propKey,
            0,
            itemIndex,
            constructChartAxesFieldObject(),
            currentChartAxesName,
          ),
        );
      } else if (from === 'reportFilter') {
        dispatch(updateChartFilterRightGroupsFilters(name, constructChartAxesFieldObject()));
        dispatch(setLastModifiedFilter(name, filterFieldData.uId));
        triggerSyncFilterDataFetch(filterFieldData, true);
      }
    }
  };

  ///Fetch Field data for Relative Filter
  const fetchRelativeFilterFieldData = () => {
    const { fieldname, displayname, dataType, tableId } = field;
    if (
      filterFieldData.expTypeToRelativeDate !== 'current' &&
      !filterFieldData.exprInputToValueType
    )
      return null;
    if (
      filterFieldData.expTypeFromRelativeDate !== 'current' &&
      !filterFieldData.exprInputFromValueType
    )
      return null;
    if (
      !filterFieldData.expTypeAnchorDate ||
      (filterFieldData.expTypeAnchorDate === 'specificDate' &&
        !filterFieldData.exprInputSpecificDate)
    )
      return null;
    const toFilterCalculation = calculations.savedCalculations.find(
      (calculation) => calculation.uuid === field.SavedCalculationUUID,
    )?.calculationInfo;

    let bodyData: any = {
      filterTable: field.SavedCalculationUUID
        ? {
            tableId: tableId,
            displayName: displayname,
            fieldName: fieldname,
            dataType: dataType,
            timeGrain: 'date',
            isCalculatedField: true,
            calculatedField: [
              {
                ...toFilterCalculation,
                calculatedFieldId: field.SavedCalculationUUID,
              },
            ],
          }
        : {
            tableId: tableId,
            displayName: displayname,
            fieldName: fieldname,
            dataType: dataType,
            timeGrain: 'date',
          },

      //Fetch Request type
      // from: ["next", "1", "day"],
      // to: ["next", "1", "day"],
      // anchorDate: "today",
      from: [
        filterFieldData.expTypeFromRelativeDate,
        filterFieldData.exprInputFromValueType,
        filterFieldData.expTypeFromdate,
      ],
      to: [
        filterFieldData.expTypeToRelativeDate,
        filterFieldData.exprInputToValueType,
        filterFieldData.expTypeTodate,
      ],
      anchorDate:
        filterFieldData.expTypeAnchorDate !== 'specificDate'
          ? filterFieldData.expTypeAnchorDate
          : filterFieldData.exprInputSpecificDate,
    };

    let _url = '';

    if (from === 'chartFilter') {
      _url = `relative-filter?dbconnectionid=${chartProp.properties[propKey].selectedDs.connectionId}&datasetid=${chartProp.properties[propKey].selectedDs.id}`;
    } else if (from === 'reportFilter') {
      if (link && link !== '') {
        _url = `open-link/relative-filter?dbconnectionid=${dsInfo.connectionId}&datasetid=${dsInfo.id}&linkId=${link}`;
      } else {
        _url = `relative-filter?dbconnectionid=${dsInfo.connectionId}&datasetid=${dsInfo.id}`;
      }
    }

    return FetchData({
      checkToken: link === '',
      requestType: 'withData',
      method: 'POST',
      url: _url,
      headers:
        link && link !== ''
          ? { 'Content-Type': 'application/json', 'X-TENANT-ID': 'community' }
          : {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
      data: bodyData,
    });
  };

  function convertKeysToLowercase<T extends Record<string, any>>(obj: T): Record<string, any> {
    return Object.keys(obj).reduce(
      (acc, key) => {
        acc[key.toLowerCase()] = obj[key];
        return acc;
      },
      {} as Record<string, any>,
    );
  }

  ///To fetch Relative Filter items
  const GetRelativeFilterItems = async () => {
    let result: any = await fetchRelativeFilterFieldData();

    if (result) {
      if (result.data && result.data.length > 0) {
        const modifiedRes = convertKeysToLowercase(result.data[0]);
        filterFieldData['fromDate'] = modifiedRes['fromdate'];
        filterFieldData['toDate'] = modifiedRes['todate'];
      }

      if (from === 'chartFilter') {
        dispatch(
          updateAxesQueryParam(
            propKey,
            0,
            itemIndex,
            constructChartAxesFieldObject(),
            currentChartAxesName,
          ),
        );
      } else if (from === 'reportFilter') {
        dispatch(updateChartFilterRightGroupsFilters(name, filterFieldData));
        dispatch(setLastModifiedFilter(name, filterFieldData.uId));
        triggerSyncFilterDataFetch(filterFieldData, true);
      }
      dispatch(callQueryAPI(propKey, true));
    } else {
      if (from === 'reportFilter') {
        filterFieldData['fromDate'] = null;
        filterFieldData['toDate'] = null;
        dispatch(updateChartFilterRightGroupsFilters(name, filterFieldData));
        dispatch(setLastModifiedFilter(name, filterFieldData.uId));
        triggerSyncFilterDataFetch(filterFieldData, true);
      }
    }
  };

  return { GetPickListItems, GetRelativeFilterItems };
};
